'use client';

import { useEffect } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

// Osano emits consent decisions as ACCEPT/DENY strings, not booleans.
// See https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#the-consent-object
type ConsentSelections = Record<string, 'ACCEPT' | 'DENY'>;

type OsanoConsentSavedListener = (selections: ConsentSelections) => void;

// Osano's pre-load command queue: calls are buffered into window.Osano.data until
// osano.js loads, then replayed. OsanoScriptTag renders the stub that defines this
// before osano.js, so registration never has to wait for the script to finish loading.
// https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#events-consent-saved
type OsanoQueue = (command: 'onConsentSaved', listener: OsanoConsentSavedListener) => void;

// The consent-manager API that osano.js attaches to window.Osano.cm once the script loads.
// Only the methods we call are typed here.
// https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api
type OsanoConsentManager = {
    // Opens the "Do not sell or share my personal information" modal.
    showDoNotSell: () => void;
    // Opens the cookie preferences drawer.
    showDrawer: () => void;
};

declare global {
    interface Window {
        // `cm` is present only after osano.js loads, so it is optional.
        Osano?: OsanoQueue & { data?: unknown[]; cm?: OsanoConsentManager };
        // Internal, page-scoped guard (see __osanoConsentSavedRegistered usage below).
        __osanoConsentSavedRegistered?: boolean;
    }
}

const logger = LoggerFactory.create('OsanoConsentListener');

async function persistConsent(selections: ConsentSelections): Promise<void> {
    try {
        const response = await fetch('/api/osano/cookie-management', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(selections),
            // Saving consent can coincide with a navigation; keepalive lets the request
            // outlive the page unload so the decision is still recorded.
            keepalive: true,
        });

        if (!response.ok) {
            logger.error({ status: response.status }, 'Osano consent persist failed');
        }
    } catch (error) {
        logger.error({ error }, 'Osano consent persist threw');
    }
}

export function OsanoConsentListener(): null {
    useEffect(() => {
        // The Osano queue has no unsubscribe, so a listener registered once lives for the
        // page's lifetime. The guard is page-scoped (on window) rather than per-component so
        // remounting never adds a duplicate handler — covering React Strict Mode's
        // double-invoked effect and navigation between route-group layouts (each layout
        // renders its own instance). A duplicate handler would mean duplicate PUTs per save.
        if (window.__osanoConsentSavedRegistered || !window.Osano) {
            return;
        }
        window.__osanoConsentSavedRegistered = true;

        // Osano replays the most recent decision immediately on subscribe, so returning
        // visitors are persisted without having to re-open the consent dialog.
        window.Osano('onConsentSaved', (selections) => void persistConsent(selections));
    }, []);

    return null;
}
